import React, { forwardRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import DatePicker from 'react-datepicker';
import { getYear, parse, isValid, format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import './datepicker-box.scss';
import { CalenderIcon, EditCalenderIcon } from '../icons/icons';
import TextBox from '../text-box/text-box';

const CustomInput = forwardRef(
  ({ selectedDate, onDateChange, placeholder, dateFormat, onClick }, ref) => {
    const [inputValue, setInputValue] = useState('');

    // Keep input in sync with selectedDate from parent
    useEffect(() => {
      if (selectedDate) {
        setInputValue(format(selectedDate, dateFormat));
      } else {
        setInputValue('');
      }
    }, [selectedDate, dateFormat]);

    const handleChange = (e) => {
      const text = e?.target?.value;
      setInputValue(text);

      const parsed = parse(text, dateFormat, new Date());
      if (isValid(parsed)) {
        onDateChange(parsed); // update parent state → calendar updates
      }
    };

    return (
      <TextBox
        placeholder={placeholder}
        value={inputValue}
        ref={ref}
        onChange={(e) => handleChange(e)}
        onClick={onClick}
        readOnly
      />
    );
  }
);

CustomInput.propTypes = {
  selectedDate: PropTypes.instanceOf(Date),
  onDateChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  dateFormat: PropTypes.string.isRequired,
  onClick: PropTypes.func,
};
CustomInput.defaultProps = {
  selectedDate: null,
  placeholder: '',
  onClick: () => {},
};

const DatepickerBox = forwardRef(
  (
    {
      id,
      label,
      errorMessage,
      required,
      selectsRange,
      isClearable,
      startDate,
      endDate,
      mBottom,
      placeholder,
      onChange,
      selectedDate,
      setDate,
      monthsShown,
      dateFormat,
      enableEdit,
      disabled,
      handleEnable,
      className,
      showTimeInput,
      mTop,
      inline,
      customHeader,
      disableIcon,
      minDate,
      maxDate,
      onKeyUp,
      infoMessage,
      calenderClassName,
    },
    ref
  ) => {
    const [t] = useTranslation('datepicker');

    const handleKeyDown = (e) => {
      const allowedKeys = [
        'Backspace',
        'Delete',
        'Tab',
        'Escape',
        'Enter',
        'Home',
        'End',
        'ArrowLeft',
        'ArrowRight',
        '/',
        '-',
        ':',
      ];
      if (
        (e?.ctrlKey === true && ['a', 'c', 'v', 'x']?.indexOf(e?.key) !== -1) ||
        allowedKeys?.indexOf(e?.key) !== -1
      ) {
        return;
      }
      if (/^\d$/.test(e?.key)) {
        return;
      }
      e.preventDefault();
    };

    const renderDayContents = (date) => {
      return <span>{date}</span>;
    };

    const currentYear = new Date()?.getFullYear();
    const years = [];
    for (let year = 1990; year <= currentYear; year++) {
      years.push(year);
    }

    return (
      <div
        className={`date-box-group ${className}`}
        style={{ marginBottom: mBottom, marginTop: mTop }}
      >
        {label && (
          <label htmlFor={id}>
            {label}
            {required && <span className="required">*</span>}
          </label>
        )}
        <div
          className={`position-relative ${showTimeInput ? 'enabled-time' : ''}`}
        >
          {enableEdit ? (
            <button
              type="button"
              className={`edit-box ${calenderClassName}`}
              onClick={handleEnable}
            >
              <EditCalenderIcon />
            </button>
          ) : (
            !disableIcon && (
              <button
                type="button"
                className={`edit-box disabled ${calenderClassName}`}
              >
                <CalenderIcon size="1.25vw" color="#3B86F2" />
              </button>
            )
          )}
          {customHeader ? (
            <DatePicker
              renderCustomHeader={({
                date,
                monthDate,
                customHeaderCount,
                changeYear,
                decreaseMonth,
                increaseMonth,
              }) => (
                <div>
                  <button
                    aria-label="Previous Month"
                    className="react-datepicker__navigation react-datepicker__navigation--previous"
                    style={
                      customHeaderCount === 1 ? { visibility: 'hidden' } : null
                    }
                    onClick={decreaseMonth}
                    type="button"
                  >
                    <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">
                      {'<'}
                    </span>
                  </button>
                  <span className="react-datepicker__current-month">
                    {monthDate?.toLocaleString('en-US', {
                      month: 'long',
                    })}
                    <select
                      value={getYear(date)}
                      onChange={({ target: { value } }) => changeYear(value)}
                    >
                      {years.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </span>
                  <button
                    aria-label="Next Month"
                    className="react-datepicker__navigation react-datepicker__navigation--next"
                    style={
                      customHeaderCount === 0 ? { visibility: 'hidden' } : null
                    }
                    onClick={increaseMonth}
                    type="button"
                  >
                    <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">
                      {'>'}
                    </span>
                  </button>
                </div>
              )}
              selectsRange={selectsRange}
              monthsShown={monthsShown}
              isClearable={isClearable}
              startDate={startDate}
              endDate={endDate}
              placeholderText={placeholder}
              dateFormat={dateFormat}
              disabled={disabled}
              timeInputLabel={`${t('datePickerscreen.time')}:`}
              showTimeInput={showTimeInput}
              inline={inline}
              renderDayContents={renderDayContents}
              selected={selectedDate}
              onChange={(e) => {
                onChange(e);
                setDate(e);
              }}
              onKeyDown={handleKeyDown}
            />
          ) : (
            <DatePicker
              selected={selectedDate}
              onChange={(e) => {
                onChange(e);
                setDate(e);
              }}
              selectsRange={selectsRange}
              monthsShown={monthsShown}
              isClearable={isClearable}
              startDate={startDate}
              endDate={endDate}
              placeholderText={placeholder}
              dateFormat={dateFormat}
              disabled={disabled}
              timeInputLabel={`${t('datePickerscreen.time')}:`}
              showTimeInput={showTimeInput}
              inline={inline}
              renderDayContents={renderDayContents}
              onKeyDown={handleKeyDown}
              minDate={minDate}
              maxDate={maxDate}
              ref={ref}
              onKeyUp={onKeyUp}
              focusSelectedMonth={true}
              customInput={
                <CustomInput
                  selectedDate={selectedDate}
                  onDateChange={setDate}
                  placeholder={placeholder}
                  dateFormat={dateFormat}
                />
              }
              popperProps={{
                strategy: 'fixed',
              }}
              popperContainer={({ children }) =>
                ReactDOM.createPortal(children, document.body)
              }
              calendarClassName={showTimeInput ? 'enabled-time' : ''}
            />
          )}
        </div>
        {errorMessage !== '' && <p className="error-message">{errorMessage}</p>}
        {infoMessage !== '' && <p className="info-message">{infoMessage}</p>}
      </div>
    );
  }
);

DatepickerBox.propTypes = {
  label: PropTypes.string,
  id: PropTypes.string,
  errorMessage: PropTypes.string,
  required: PropTypes.bool,
  selectsRange: PropTypes.bool,
  isClearable: PropTypes.bool,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  mBottom: PropTypes.string,
  placeholder: PropTypes.string,
  selectedDate: PropTypes.instanceOf(Date).isRequired,
  onChange: PropTypes.func,
  setDate: PropTypes.func,
  className: PropTypes.string,
  monthsShown: PropTypes.number,
  dateFormat: PropTypes.string,
  enableEdit: PropTypes.bool,
  disabled: PropTypes.bool,
  handleEnable: PropTypes.func,
  showTimeInput: PropTypes.bool,
  mTop: PropTypes.string,
  inline: PropTypes.bool,
  customHeader: PropTypes.bool,
  disableIcon: PropTypes.bool,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  onKeyUp: PropTypes.func,
  infoMessage: PropTypes.string,
  calenderClassName: PropTypes.string,
};
DatepickerBox.defaultProps = {
  label: '',
  id: '',
  className: '',
  errorMessage: '',
  required: false,
  selectsRange: false,
  isClearable: false,
  startDate: '',
  endDate: '',
  mBottom: '',
  placeholder: '',
  onChange: () => {},
  setDate: () => {},
  monthsShown: 1,
  dateFormat: 'dd/MM/yyyy',
  enableEdit: false,
  disabled: false,
  handleEnable: () => {},
  showTimeInput: false,
  mTop: '',
  inline: false,
  customHeader: false,
  disableIcon: false,
  minDate: null,
  maxDate: null,
  onKeyUp: () => {},
  infoMessage: '',
  calenderClassName: '',
};

export default DatepickerBox;
